import React, {
    createContext,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {useTranslation} from 'react-i18next';
import i18next from 'i18next';

import type {Participant} from '@pexip/infinity';
import type {RPCCall, RPCReply} from '@pexip/plugin-api';
import {isRPCCall, Channel} from '@pexip/plugin-api';

import {useBranding} from '../branding/Context';
import {logger} from '../logger';
import {userInitiatedDisconnectSignal} from '../signals/InMeeting.signals';
import {useInfinityContext} from '../hooks/useInfinityContext';
import type {Plugin} from '../branding';

import type {ChannelContext, PluginContext} from './types';
import {handleInfinityCall} from './infinityCalls';
import {registerInfinityClientSignals} from './signals/RegisterInfinityClient.signals';
import {
    handleAddButton,
    handleOpenForm,
    handleOpenPrompt,
    handleRemoveElement,
    handleShowToast,
    handleUpdateButton,
    handleAddWidget,
    handleToggleWidget,
} from './handleUiRPC';
import {getValidSandboxValues} from './utils/sandbox.utils';
import {handleSetDisconnectDestination} from './handleAppRPC';

export const Plugins = createContext<PluginContext>({});

export const Channels = createContext<
    React.RefObject<ChannelContext> | undefined
>(undefined);

export const WidgetIframes = createContext<
    React.RefObject<Set<HTMLIFrameElement>> | undefined
>(undefined);

export const PluginManager: React.FC<React.PropsWithChildren> = ({
    children,
}) => {
    const {i18n} = useTranslation();
    const plugins = useBranding('plugins');
    const pluginIframes = useRef(new Set<HTMLIFrameElement>());
    const widgetIframes = useRef(new Set<HTMLIFrameElement>());
    const loadedPlugins = useRef(new Array<Plugin>());

    const activePlugins = useMemo(
        () =>
            (plugins ?? []).map(plugin => (
                // biome-ignore lint/a11y/useIframeTitle: the plugins are only logical and not visible
                <iframe
                    id={plugin.id}
                    key={`plugin:${plugin.src}`}
                    sandbox={getValidSandboxValues(plugin)}
                    src={plugin.src}
                    aria-hidden
                    className="b-zero"
                    ref={ref => {
                        if (ref) {
                            pluginIframes.current.add(ref);
                            loadedPlugins.current.push(plugin);
                        }
                    }}
                />
            )),
        [plugins],
    );
    const channels = useRef(new Map<string, Channel>());
    const [pluginsElements, setPluginsElements] = useState<PluginContext>({});
    const infinity = useInfinityContext();

    useEffect(() => {
        for (const channel of channels.current.values()) {
            channel.sendEvent({
                event: 'event:languageSelect',
                payload: i18n.language,
            });
        }
    }, [i18n.language]);

    useEffect(() => {
        const detachSignals = registerInfinityClientSignals(
            channels.current,
            infinity,
        );
        detachSignals.push(
            userInitiatedDisconnectSignal.add(() => {
                for (const channel of channels.current.values()) {
                    channel.sendEvent({
                        event: 'event:userInitiatedDisconnect',
                        payload: undefined,
                    });
                }
            }),
        );
        return () => {
            for (const detachSignal of detachSignals) {
                detachSignal();
            }
        };
    }, [infinity]);

    const isMessageSourceAnIframe = useCallback(
        (
            source: MessageEventSource | null,
            iframes: Set<HTMLIFrameElement>,
        ) => {
            for (const iframe of iframes) {
                if (iframe.contentWindow && iframe.contentWindow === source) {
                    return true;
                }
            }
            return false;
        },
        [],
    );

    const isPluginIframe = useCallback(
        (source: MessageEventSource | null) =>
            isMessageSourceAnIframe(source, pluginIframes.current),
        [isMessageSourceAnIframe],
    );

    const isWidgetIframe = useCallback(
        (source: MessageEventSource | null) =>
            isMessageSourceAnIframe(source, widgetIframes.current),
        [isMessageSourceAnIframe],
    );

    const findManifestPluginByChannel = useCallback(
        (chanId: string) =>
            loadedPlugins.current.find(
                plugin => plugin?.id && chanId.startsWith(`${plugin.id}-`),
            ),

        [],
    );

    const validateSyn = useCallback(
        (
            payload: RPCCall<'syn'>['payload'],
            source: MessageEventSource | null,
        ): RPCReply<'syn'>['payload'] => {
            if (!isPluginIframe(source)) {
                return {
                    ack: false,
                    reason: 'Not a plugin. You are likely trying to register a widget as a plugin',
                };
            }
            for (const key of channels.current.keys()) {
                if (key.startsWith(`${payload.id}-`)) {
                    return {
                        ack: false,
                        reason: 'A plugin with the same id already exists',
                    };
                }
            }

            return {ack: true};
        },
        [isPluginIframe],
    );

    const validateSynWidget = useCallback(
        (
            source: MessageEventSource | null,
        ): RPCReply<'syn:widget'>['payload'] => {
            if (!isWidgetIframe(source)) {
                return {
                    ack: false,
                    reason: 'Not a widget. You are likely trying to register a plugin as a widget',
                };
            }

            return {ack: true};
        },
        [isWidgetIframe],
    );

    const setupChannel = useCallback(
        (data: RPCCall<'syn' | 'syn:widget'>, newChannel: Channel) => {
            channels.current.set(data.chanId, newChannel);
            setPluginsElements(pluginsElements => ({
                ...pluginsElements,
                [data.chanId]: {
                    buttons: [],
                    forms: [],
                    prompts: [],
                    widgets: [],
                },
            }));
            newChannel.replyRPC({
                rpc: data.rpc,
                replyTo: data.id,
                payload: {ack: true},
            });
        },
        [],
    );

    const syncPlugin = useCallback((channel: Channel) => {
        channel.sendEvent({
            event: 'event:languageSelect',
            payload: i18next.language,
        });
    }, []);

    const syncWidget = useCallback(
        (channel: Channel) => {
            const meeting = infinity.getMeeting();
            for (const [roomId, status] of meeting.getConferenceStatus()) {
                channel.sendEvent({
                    event: 'event:conferenceStatus',
                    payload: {status, id: roomId},
                });
            }
            channel.sendEvent({
                event: 'event:conference:authenticated',
                payload: {conferenceAlias: meeting.getConferenceAlias()},
            });
            channel.sendEvent({
                event: 'event:languageSelect',
                payload: i18next.language,
            });
            for (const roomId of meeting.getRooms()) {
                const pids = meeting.getParticipants({
                    roomId: meeting.getRoomId(),
                });
                const participants: Participant[] = [];
                for (const pid of pids) {
                    const participant = meeting.getParticipant(
                        pid,
                        meeting.getRoomId(),
                    );
                    if (participant) {
                        participants.push(participant);
                    }
                }
                channel.sendEvent({
                    event: 'event:participants',
                    payload: {
                        id: roomId,
                        participants,
                    },
                });
                const meInRoom = meeting.getMe(roomId);
                if (meInRoom) {
                    channel.sendEvent({
                        event: 'event:me',
                        payload: {id: roomId, participant: meInRoom},
                    });
                }
            }
        },
        [infinity],
    );

    useEffect(() => {
        const onMessage = ({source, data}: MessageEvent<RPCCall>) => {
            if (!isRPCCall(data)) {
                return;
            }
            if (!isPluginIframe(source) && !isWidgetIframe(source)) {
                return;
            }
            const chanId = data.chanId;
            if (data.rpc === 'syn') {
                const newChannel = new Channel(source as Window, chanId);
                const response = validateSyn(data.payload, source);
                if (!response.ack) {
                    newChannel.replyRPC({
                        rpc: data.rpc,
                        replyTo: data.id,
                        payload: response,
                    });
                    newChannel.unregister();
                    return;
                }

                logger.debug({data}, 'Registering new plugin');
                setupChannel(data, newChannel);
                syncPlugin(newChannel);
                return;
            }
            if (data.rpc === 'syn:widget') {
                const newChannel = new Channel(source as Window, chanId);
                const response = validateSynWidget(source);
                if (!response.ack) {
                    newChannel.replyRPC({
                        rpc: data.rpc,
                        replyTo: data.id,
                        payload: response,
                    });
                    newChannel.unregister();
                    return;
                }

                logger.debug({data}, 'Registering new widget');
                setupChannel(data, newChannel);
                syncWidget(newChannel);
                return;
            }
            const channel = channels.current.get(chanId);
            if (channel) {
                logger.debug({data}, 'Emit reply for plugin message');
                switch (data.rpc) {
                    case 'app:setDisconnectDestination': {
                        channel.replyRPC(handleSetDisconnectDestination(data));
                        break;
                    }
                    case 'ui:button:add': {
                        channel.replyRPC(
                            handleAddButton(data, setPluginsElements),
                        );
                        break;
                    }
                    case 'ui:button:update': {
                        channel.replyRPC(
                            handleUpdateButton(data, setPluginsElements),
                        );
                        break;
                    }
                    case 'ui:form:open': {
                        channel.replyRPC(
                            handleOpenForm(data, setPluginsElements),
                        );
                        break;
                    }
                    case 'ui:toast:show': {
                        channel.replyRPC(handleShowToast(data));
                        break;
                    }
                    case 'ui:prompt:open': {
                        channel.replyRPC(
                            handleOpenPrompt(data, setPluginsElements),
                        );
                        break;
                    }
                    case 'ui:removeElement': {
                        channel.replyRPC(
                            handleRemoveElement(data, setPluginsElements),
                        );
                        break;
                    }
                    case 'ui:widget:add': {
                        channel.replyRPC(
                            handleAddWidget(
                                data,
                                setPluginsElements,
                                findManifestPluginByChannel(data.chanId),
                            ),
                        );
                        break;
                    }
                    case 'ui:widget:toggle': {
                        channel.replyRPC(handleToggleWidget(data));
                        break;
                    }
                    case 'conference:dialOut':
                    case 'conference:sendMessage':
                    case 'conference:sendApplicationMessage':
                    case 'conference:lock':
                    case 'conference:muteAllGuests':
                    case 'conference:setBandwidth':
                    case 'conference:setLayout':
                    case 'conference:disconnectAll':
                    case 'conference:sendRequest':
                    case 'conference:requestParticipants':
                    case 'conference:breakout':
                    case 'conference:joinBreakoutRoom':
                    case 'conference:closeBreakouts':
                    case 'conference:closeBreakoutRoom':
                    case 'conference:emptyBreakouts':
                    case 'conference:breakoutMoveParticipants':
                    case 'conference:currentRoomId':
                    case 'participant:transfer':
                    case 'participant:mute':
                    case 'participant:muteVideo':
                    case 'participant:disconnect':
                    case 'participant:spotlight':
                    case 'participant:admit':
                    case 'participant:setRole':
                    case 'participant:raiseHand':
                    case 'participant:setTextOverlay':
                    case 'participant:sendDTMF':
                        handleInfinityCall({
                            data,
                            channel,
                            sendMessage: infinity.getMeeting().sendMessage,
                        });
                        break;
                }
            }
        };
        window.addEventListener('message', onMessage);
        return () => {
            window.removeEventListener('message', onMessage);
        };
    }, [
        findManifestPluginByChannel,
        syncWidget,
        syncPlugin,
        setupChannel,
        infinity,
        isPluginIframe,
        isWidgetIframe,
        validateSyn,
        validateSynWidget,
    ]);

    return (
        <>
            <Channels.Provider value={channels}>
                <WidgetIframes.Provider value={widgetIframes}>
                    <Plugins.Provider value={pluginsElements}>
                        {children}
                    </Plugins.Provider>
                </WidgetIframes.Provider>
            </Channels.Provider>
            {activePlugins}
        </>
    );
};
